import type { LondonGuideContext } from './serviceAreaBlogMapping';

/** ~150–165 chars: primary for Google snippet; unique per borough via areas + postcodes */
export function buildGuideMetaDescription(ctx: LondonGuideContext): string {
  const { displayName, borough } = ctx;
  const areas = borough.keyAreas.slice(0, 3).join(', ');
  const pc = borough.postcodes.slice(0, 4).join(', ');
  const base = `Resin bound driveways in ${displayName}, London: costs, SUDS drainage, planning tips & installation — for ${areas}${pc ? `; postcodes ${pc}` : ''}. Free quotes. ZAN Resin Surfaces.`;
  if (base.length <= 165) return base;
  return `${base.slice(0, 159).trim()}…`;
}

/** Meta keywords: local + service intents (Google largely ignores; still used by some systems) */
export function buildGuideKeywords(ctx: LondonGuideContext): string {
  const b = ctx.borough;
  const name = ctx.displayName;
  const parts = [
    `resin driveways ${name}`,
    `resin bound surfacing ${name}`,
    `permeable resin driveway ${name}`,
    `SUDS compliant driveway ${name}`,
    `resin driveway cost ${name}`,
    `resin driveway installers ${name}`,
    `resin bound pathways ${name}`,
    ...b.keyAreas.slice(0, 5).map((a) => `resin surfacing ${a}`),
    ...b.postcodes.slice(0, 8),
    'resin bound surfacing London',
    'permeable driveway Greater London',
  ];
  return Array.from(new Set(parts)).join(', ');
}

export function getLocalLeadParagraph(ctx: LondonGuideContext): string {
  const { routeSlug, displayName, borough } = ctx;
  const k = borough.keyAreas.slice(0, 6).join(', ');
  if (routeSlug === 'kensington') {
    return `Kensington combines garden squares, embassy-grade residences, and busy retail streets. Homeowners here often want driveways and forecourts that stay neat under daily use while meeting modern drainage rules. ${borough.description} This guide focuses on what resin bound surfacing delivers for properties around ${k} and nearby.`;
  }
  if (routeSlug === 'chelsea') {
    return `Chelsea mixes classic stucco terraces, mews houses, and newer developments close to the Thames. Resin bound aggregate can follow curves, follow slopes, and stay permeable without loose gravel escaping onto pavements. Below we cover practical decisions for projects near ${k} and across Chelsea.`;
  }
  if (routeSlug === 'hammersmith') {
    return `Hammersmith sits at a major junction of roads and public transport. Driveways and commercial forecourts here need to drain well and withstand turning traffic. ${borough.description} We explain how resin bound systems perform for streets around ${k}.`;
  }
  if (routeSlug === 'fulham') {
    return `Fulham’s streets range from Victorian terraces to riverside apartments. Many plots are compact, so every square metre of usable parking counts — and permeable resin helps avoid adding surface water to drains. This article is written for homeowners and landlords near ${k}.`;
  }
  return `${borough.description} From ${borough.keyAreas[0] ?? displayName} through ${k.split(', ').slice(-2).join(' and ')}, resin bound surfacing is increasingly chosen where tarmac looks too industrial and block paving needs too much maintenance. The sections below walk through suitability, rules, pricing, and what good installation looks like in ${displayName}.`;
}

export function buildGuideFaqs(ctx: LondonGuideContext): { question: string; answer: string }[] {
  const { displayName, borough } = ctx;
  const samplePostcodes = borough.postcodes.slice(0, 6).join(', ');
  return [
    {
      question: `How much does a resin bound driveway cost in ${displayName}?`,
      answer: `Indicative installed costs in Greater London often fall between roughly £65 and £95 per square metre depending on substrate condition, area size, edgings, and aggregate choice. Tight London frontages with awkward access, waste removal through the house, or full dig-out of failed bases can sit above that range. We provide itemised quotes after a site visit covering ${displayName} postcodes such as ${samplePostcodes}.`,
    },
    {
      question: `Do resin driveways help with SUDS and front garden rules in London?`,
      answer: `A correctly installed resin bound system is water-permeable: rain passes through to the ground below (subject to a suitable base build-up). That supports Sustainable Urban Drainage (SUDS) thinking and is often relevant when replacing impermeable front gardens. Rules vary by street and designation; we flag conservation and estate restrictions during survey.`,
    },
    {
      question: `How long does installation take on a typical ${displayName} driveway?`,
      answer: `Many domestic driveways are completed in one to three days once the base is sound. If the existing surface is failing, we may need to excavate and rebuild foundations, which extends the programme but prevents premature failure of the finished resin layer.`,
    },
    {
      question: `Can resin bound surfacing handle slopes and tree roots?`,
      answer: `Yes, with correct specification. Stepped or ramped approaches, channel drainage, and root management are part of good design — especially on older London plots. We assess falls, services, and trees on site before recommending a build-up.`,
    },
    {
      question: `Why use a specialist installer for resin bound work?`,
      answer: `Resin bound is mixed and laid to a tight window; batching, temperature, and compaction all affect longevity. Professional installation reduces risks of softness, shiny patches, or loose stone — and keeps your warranty meaningful in ${displayName} and across London.`,
    },
    {
      question: `Do I need planning permission for a resin driveway in ${displayName}?`,
      answer: `Many permeable domestic driveways fall within permitted development, but rules depend on whether the surface is permeable, the size of the area, whether you are paving a front garden, and if the property is listed or in a designated area. Your local planning authority has the final say; we highlight common triggers during survey and can suggest compliant specifications.`,
    },
    {
      question: `Can resin bound surfacing be used for commercial forecourts in ${displayName}?`,
      answer: `Yes. Car parks, school yards, and retail forecourts often need heavier-duty base build-ups and sometimes larger aggregate grades. We specify systems for the expected traffic and loading, including delivery bays and wheelchair-accessible routes where relevant.`,
    },
    {
      question: `Is winter a bad time to install resin bound surfacing in London?`,
      answer: `Installation is possible in cooler months, but resin cure and working times are sensitive to temperature and moisture. We monitor forecasts and may adjust scheduling to avoid frost, heavy rain, or damp substrates that could trap moisture beneath the finish.`,
    },
  ];
}
